// Cost anomaly feed — surfaces the alerts the backend anomaly.dispatcher already
// computes (COST_SPIKE / LABEL_SPIKE in alert_history). Read-only.

import React from 'react';
import { Card, CardContent } from '../ui/card';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useAnomalies } from '../../hooks/useFinopsV2';
import { formatDateTime } from './finopsFormat';
import { SectionHeader, Skeleton } from './finopsUi';

const SEV_TINT = {
  critical: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10',
  serious: 'text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-500/10',
  warning: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10',
  info: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10',
};

export default function AnomalyFeed({ clusterId, days = 14 }) {
  const { data, loading } = useAnomalies(clusterId, days);
  const anomalies = data?.anomalies || [];

  return (
    <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
      <CardContent className="p-5">
        <SectionHeader title="Cost Anomalies" hint={`Detected spikes · last ${days} days`} />
        {loading ? (
          <Skeleton className="h-24" />
        ) : anomalies.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-text-secondary py-4">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            No cost anomalies detected in this period.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {anomalies.map((a, i) => {
              const tint = SEV_TINT[(a.severity || '').toLowerCase()] || SEV_TINT.warning;
              return (
                <li key={i} className="flex items-start gap-3 py-2.5">
                  <span className={`mt-0.5 p-1.5 rounded-md ${tint}`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{a.title || 'Cost spike'}</p>
                      <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">{formatDateTime(a.fired_at)}</span>
                    </div>
                    {a.body && <p className="text-xs text-slate-500 dark:text-text-secondary mt-0.5 line-clamp-2">{a.body}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
